import { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Heart, MagnifyingGlass, Play } from '@phosphor-icons/react';
import { drumLanguageAtom, drumSelectedCognitiveAtom } from '../../shared/store/drumAppAtoms';
import { fetchPublicCognitiveActivities } from '../../shared/api/memberApi';
import type { CognitiveActivity } from '../../shared/data/cognitive';
import { centerInfiniteRailItem } from '../../shared/utils/drumHelpers';
import { useFavorites } from '../../shared/hooks/useFavorites';
import './Cognitive.scss';

function Cognitive() {
  const language = useAtomValue(drumLanguageAtom);
  const [selectedActivity, setSelectedActivity] = useAtom(drumSelectedCognitiveAtom);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const { favoriteIds, toggleFavorite } = useFavorites('cognitive');
  const [activities, setActivities] = useState<CognitiveActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const listRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchPublicCognitiveActivities()
      .then((items) => {
        if (cancelled) return;
        setActivities(items);
        setErrorMessage('');
        setSelectedActivity((current) => current && items.some((item) => item.id === current.id)
          ? items.find((item) => item.id === current.id) || null
          : null);
      })
      .catch((error) => {
        if (cancelled) return;
        setActivities([]);
        setSelectedActivity(null);
        setErrorMessage(error instanceof Error ? error.message : (language === 'ko' ? '인지 활동을 불러오지 못했습니다.' : 'Unable to load activities.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [language, setSelectedActivity]);

  const normalizedQuery = query.trim().toLowerCase();
  const visible = activities.filter((activity) => {
    const matchesCategory = category === 'all' || (category === 'favorites' && favoriteIds.includes(activity.id));
    return matchesCategory && `${activity.titleKo} ${activity.titleEn} ${activity.descriptionKo} ${activity.descriptionEn}`.toLowerCase().includes(normalizedQuery);
  });
  const selectedFavorite = selectedActivity ? favoriteIds.includes(selectedActivity.id) : false;
  const emptyCopy = errorMessage
    || (isLoading
      ? (language === 'ko' ? '인지 활동을 불러오는 중...' : 'Loading activities...')
      : category === 'favorites'
        ? (language === 'ko' ? '즐겨찾기한 인지 활동이 없습니다.' : 'No favorite activities yet.')
        : normalizedQuery
          ? (language === 'ko' ? '검색 결과가 없습니다.' : 'No activities found.')
          : (language === 'ko' ? '등록된 인지 활동이 없습니다.' : 'No activities yet.'));

  const selectCategory = (nextCategory: string) => {
    setCategory(nextCategory);
    setSelectedActivity(null);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
      listRef.current.scrollLeft = 0;
    }
  };

  const selectActivity = (activity: CognitiveActivity, target: HTMLElement) => {
    setSelectedActivity(activity);
    window.requestAnimationFrame(() => centerInfiniteRailItem(listRef.current, target));
  };

  const beginActivity = () => {
    if (!selectedActivity?.playable) return;
    navigate('/drum/cognitive-game');
  };

  return (
    <div className="page cognitive-catalog">
      <div className="cognitive-tools">
        <label><MagnifyingGlass /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={language === 'ko' ? '인지 활동 검색' : 'Search activities'} /></label>
        <div className="category-rail">
          <button type="button" className={category === 'favorites' ? 'active' : ''} onClick={() => selectCategory('favorites')}>
            <Heart weight={category === 'favorites' ? 'fill' : 'regular'} />
            {language === 'ko' ? '즐겨찾기' : 'Favorites'}
          </button>
          <button type="button" className={category === 'all' ? 'active' : ''} onClick={() => selectCategory('all')}>
            {language === 'ko' ? '전체' : 'All'}
          </button>
        </div>
        <div className="cognitive-list-count">{language === 'ko' ? `전체 ${visible.length}개` : `${visible.length} ACTIVITIES`}</div>
      </div>
      <div className="cognitive-library-grid">
        <div className="scroll-rail-shell">
          <aside className="cognitive-activity-list" ref={listRef as React.RefObject<HTMLElement>}>
            {visible.length ? visible.map((activity) => (
              <button type="button" data-activity-id={activity.id} className={`cognitive-activity-card ${selectedActivity?.id === activity.id ? 'active' : ''}`} key={activity.id} onClick={(event) => selectActivity(activity, event.currentTarget)}>
                {activity.thumbnail ? <img src={activity.thumbnail} alt="" /> : <span className="cognitive-thumb-fallback" aria-hidden="true" />}
                {activity.freeAccess && <em className="cognitive-free-badge">FREE</em>}
                <div><strong>{language === 'ko' ? activity.titleKo : activity.titleEn}</strong><small>{language === 'ko' ? activity.descriptionKo : activity.descriptionEn}</small></div>
                <b title={language === 'ko' ? `${activity.stages}단계` : `${activity.stages} stages`}>{language === 'ko' ? `${activity.stages}단계` : `${activity.stages} STAGES`}</b>
              </button>
            )) : <p className="cognitive-empty">{emptyCopy}</p>}
          </aside>
        </div>
        {selectedActivity ? (
          <section className="cognitive-selection-summary">
            <div className="cognitive-selection-cover">
              {selectedActivity.thumbnail ? <img src={selectedActivity.thumbnail} alt="" /> : <span className="cognitive-thumb-fallback" aria-hidden="true" />}
              <button className={`favorite-button cognitive-favorite ${selectedFavorite ? 'active' : ''}`} onClick={() => toggleFavorite(selectedActivity.id)} aria-label="Favorite" aria-pressed={selectedFavorite}><Heart weight={selectedFavorite ? 'fill' : 'regular'} /></button>
              {selectedActivity.freeAccess && <em className="cognitive-selection-free">FREE</em>}
            </div>
            <div className="cognitive-selection-copy">
              <span>{language === 'ko' ? selectedActivity.categoryKo : selectedActivity.categoryEn}</span>
              <h1>{language === 'ko' ? selectedActivity.titleKo : selectedActivity.titleEn}</h1>
              <p>{language === 'ko' ? selectedActivity.descriptionKo : selectedActivity.descriptionEn}</p>
              <small>{language === 'ko' ? `${selectedActivity.stages}단계 · 실행 후 레벨 선택` : `${selectedActivity.stages} STAGES · CHOOSE LEVEL AFTER START`}</small>
            </div>
            <button type="button" className="cognitive-play-button" aria-disabled={!selectedActivity.playable} onClick={beginActivity}><Play weight="fill" /><strong>PLAY</strong></button>
          </section>
        ) : (
          <section className="cognitive-selection-summary cognitive-selection-empty">
            <strong>{language === 'ko' ? '진행할 인지 활동을 선택하세요' : 'Select a cognitive activity'}</strong>
            <small>{language === 'ko' ? '활동을 선택하면 단계와 실행 정보를 확인할 수 있습니다.' : 'Choose an activity to view its stages and play details.'}</small>
          </section>
        )}
      </div>
    </div>
  );
}

export default Cognitive;
